// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Conservative allowlist-based HTML sanitizer for advisor-authored proposal
 * plan blocks (pension-proposal-builder-v2-prd.md §P6).
 *
 * Advisor-ui has no general HTML-sanitizing dependency (rehype-sanitize is
 * markdown-pipeline-specific), so — mirroring the client-ui plan renderer —
 * this walks the DOM produced by the browser's own parser and keeps only a
 * small allowlist of structural/inline tags. Everything else is dropped:
 * scripts, styles, iframes and other embedding tags are removed together
 * with their content; unknown-but-harmless elements are unwrapped (their
 * children are kept). All attributes are stripped except a safe `href` on
 * links.
 */

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'HR',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'S',
  'SUB',
  'SUP',
  'UL',
  'OL',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'A',
  'SPAN',
  'DIV',
  'TABLE',
  'THEAD',
  'TBODY',
  'TFOOT',
  'TR',
  'TH',
  'TD',
]);

/** Tags whose entire subtree is dangerous and must be removed outright. */
const DROP_WITH_CONTENT = new Set([
  'SCRIPT',
  'STYLE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'LINK',
  'META',
  'BASE',
  'FORM',
  'INPUT',
  'BUTTON',
  'SELECT',
  'TEXTAREA',
  'TEMPLATE',
  'NOSCRIPT',
]);

const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

function sanitizeElement(el: Element): void {
  // Snapshot children first — we mutate the tree as we walk.
  const children = Array.from(el.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      // Comments, CDATA, processing instructions — drop.
      child.parentNode?.removeChild(child);
      continue;
    }

    const childEl = child as Element;
    const tag = childEl.tagName.toUpperCase();

    if (DROP_WITH_CONTENT.has(tag)) {
      childEl.remove();
      continue;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Unknown but harmless (e.g. <section>, <font>): unwrap — keep the
      // children, drop the element itself.
      sanitizeElement(childEl);
      while (childEl.firstChild) {
        childEl.parentNode?.insertBefore(childEl.firstChild, childEl);
      }
      childEl.remove();
      continue;
    }

    // Allowed element: strip every attribute except a safe link href.
    for (const attr of Array.from(childEl.attributes)) {
      if (
        tag === 'A' &&
        attr.name.toLowerCase() === 'href' &&
        SAFE_HREF.test(attr.value.trim())
      ) {
        continue;
      }
      childEl.removeAttribute(attr.name);
    }
    if (tag === 'A' && childEl.getAttribute('href')) {
      childEl.setAttribute('target', '_blank');
      childEl.setAttribute('rel', 'noopener noreferrer');
    }

    sanitizeElement(childEl);
  }
}

/** Sanitize an advisor-authored HTML fragment for safe rendering. */
export function sanitizeHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  sanitizeElement(doc.body);
  return doc.body.innerHTML;
}
